import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { UserProvider, useUser } from "./context/UserContext.js";
import { ClaimProvider, useClaim } from "./context/ClaimContext.js";
import { InvoiceProvider } from "./context/InvoiceContext.js";
import { AdminUserProvider, useAdminUser } from "./context/AdminUserContext.js";
import { JobProvider } from "./context/JobContext.js";
import { SearchWorkersProvider } from "./context/SearchWorkersContext.js";
import { JobCreateProvider } from "./context/JobCreateContext.js";
import { PSChatProvider } from "./context/PsChatMessageContext.js";
import { NotificationProvider } from "./context/NotificationsContext.js";
import { ApiMetricsProvider } from "./context/ApiMetricsContext.js";
import { AgreementProvider } from "./context/AgreementContext.js";
import { SessionProvider } from "./context/SessionContext.js";
import { AnalyticsProvider } from "./context/AnalyticsContext.js";
import { TaskSenseProvider } from "./context/TaskSenseContext";
import {
  connectAdminSocket,
  disconnectAdminSocket,
  connectClientSocket,
  disconnectClientSocket,
  connectWorkerSocket,
  disconnectWorkerSocket,
} from "./websockets/UserSocketService.js";
import {
  connectClaimSocket,
  disconnectClaimSocket,
} from "./websockets/ClaimSocketService.js";
import "./appstyles/main.css";
import Home from "./components/Home.js";
import Login from "./components/login/Login.js";
import AdminLogin from "./components/administration/AdminLoginComponent.js";
import AdminAccountRegister from "./components/administration/AdminAccountHolderForm";
import PSDashboardComponent from "./components/ps-account/PSDashboardComponent";
import FSDashboardComponent from "./components/fs-account/FSDashboardComponent";
import JobsComponent from "./components/ps-account/JobsComponent";
import PSInboxComponent from "./components/ps-account/inbox/PSInboxComponent";
import NewsAreaComponent from "./components/ps-account/news-area/NewsAreaComponent.js";
import MyClientsComponent from "./components/ps-account/MyClientsComponent";
import MyJobsComponent from "./components/ps-account/jobs-section/discover-jobs/MyJobsComponent";
import ManageWorkersComponent from "./components/fs-account/jobs-section/manage-workers/ManageWorkersComponent";
import PSAccountSettingsComponent from "./components/ps-account/profile/account-settings/PSAccountSettingsComponent";
import FSAccountSettingsComponent from "./components/fs-account/profile/account-settings/FSAccountSettingsComponent";
import SupportHoursClaimComponent from "./components/ps-account/billing/support-claims/SupportHoursClaimComponent";
import FindSupportRegisterSteps from "./components/findsupport/FindSupportRegisterSteps";
import ProvideSupportRegisterSteps from "./components/providesupport/ProvideSupportRegisterSteps";
import AccountHolderForm from "./components/appforms/AccountHolderForm";
import WhatBringsYou from "./components/WhatBringsYou";
import MeRegisterStep from "./components/MeRegisterStep";
import FriendFamilyMemberStep from "./components/FriendFamilyMemberStep";
import MainForm from "./components/reg-mltstp-test/MainForm";
import PSAccountDashboard from "./components/ps-account/PSAccountDashboard";
import FSAccountDashboard from "./components/fs-account/FSAccountDashboard";
import PostJobsComponent from "./components/fs-account/jobs-section/PostJobsComponent";
import JobTypeComponent from "./components/fs-account/jobs-section/JobTypeComponent";
import FSInboxComponent from "./components/fs-account/inbox/FSInboxComponent";
import SearchWorkersComponent from "./components/fs-account/searchworkers/SearchWorkersComponent";
import JobWorkerStepsComponent from "./components/fs-account/jobs-section/JobWorkerStepsComponent";
import PostJobLocationComponent from "./components/fs-account/jobs-section/PostJobLocationComponent";
import TaskSenseCraftStep from "./components/fs-account/jobs-section/task-sense/TaskSenseJobCraftComponent";
import WorkerEmailVerificationPage from "./components/login/WorkerEmailVerificationPage";
import ClientEmailVerificationPage from "./components/login/ClientEmailVerificationPage";
import AvailabilityComponent from "./components/ps-account/profile/AvailabilityComponent";
import IndicativeRatesComponent from "./components/ps-account/profile/IndicativeRatesComponent";
import ProfileLocationsComponent from "./components/ps-account/profile/LocationsComponent";
import ExperienceComponent from "./components/ps-account/profile/ExperienceComponent";
import BankAccountComponent from "./components/ps-account/profile/BankAccountComponent";
import WorkHistoryComponent from "./components/ps-account/profile/WorkHistoryComponent";
import EducationAndTrainingComponent from "./components/ps-account/profile/EducationAndTrainingComponent";
import BadgesComponent from "./components/ps-account/profile/BadgesComponent";
import ImmunizationComponent from "./components/ps-account/profile/ImmunizationComponent";
import LanguagesComponent from "./components/ps-account/profile/LanguagesComponent";
import CulturalBackgroundComponent from "./components/ps-account/profile/CulturalBackgroundComponent";
import ReligionComponent from "./components/ps-account/profile/ReligionComponent";
import InterestsAndHobbiesComponent from "./components/ps-account/profile/InterestsAndHobbiesComponent";
import MyPreferencesComponent from "./components/ps-account/profile/MyPreferencesComponent";
import PhotoComponent from "./components/ps-account/profile/PhotoComponent";
import BioComponent from "./components/ps-account/profile/BioComponent";
import SupportHoursClaimForm from "./components/ps-account/billing/support-claims/SupportClaimForm";
import PSProfileComponent from "./components/ps-account/profile/PSProfileComponent";
import AgreementSetupComponent from "./components/ps-account/agreements/AgreementSetupComponent";
import AgreementReviewComponent from "./components/ps-account/agreements/AgreementReviewComponent";
import CalendarComponent from "./components/fs-account/CalendarComponent";
import AgreementArea from "./components/shared-components/AgreementsArea";
import MeetingOptionsComponent from "./components/shared-components/MeetingScheduleOptions";
import MeetingComponent from "./components/shared-components/MeetingComponent";
import FSProfileComponent from "./components/fs-account/profile/FSProfileComponent";
import FSPaymentDetailsComponent from "./components/fs-account/profile/FSPaymentDetailsComponent";
import FSPersonalDetailsComponent from "./components/fs-account/profile/FSPersonalDetailsComponent";
import FSSupportClaimsComponent from "./components/fs-account/support-claims/FsSupportClaimsComponent.js";
import FSMyContactsComponent from "./components/fs-account/profile/FSMyContactsComponent";
import MainAdminDashboardComponent from "./components/administration/MainAdminDashboardComponent";
import SuperAdminDashboardComponent from "./components/administration/SuperAdminDashboardComponent";
import AdminAccountDashboard from "./components/administration/AdminAccountDashboard";
import AdminDashboardComponent from "./components/administration/AdminDashboardComponent";
import AdminBillingComponent from "./components/administration/billing_invoicing/AdminBillingComponent";
import UsersWelcomeComponent from "./components/administration/users/UsersWelcomeComponent";
import WorkerUsersComponent from "./components/administration/users/WorkerUsersComponent";
import ClientUsersComponent from "./components/administration/users/ClientUsersComponent";
import PasswordResetComponent from "./components/login/PasswordResetComponent";
import PasswordEnterNewComponent from "./components/login/PasswordEnterNewComponent";
import JobsAppliedContent from "./components/administration/users/AppliedJobsComponent";
import JobsPostedContent from "./components/administration/users/PostedJobsComponent.js";
import UsersWelcomeIndexComponent from "./components/administration/users/UsersWelcomeIndexComponent";
import AdminJobListComponent from "./components/administration/jobs-area/AdminJobListComponent";
import AdminMessagesComponent from "./components/administration/messages_area/AdminMessagesComponent.js";
import AdminNotificationsComponent from "./components/administration/notifications_area/AdminNotificationsComponent.js";
import WorkerTimesheetBillingComponent from "./components/ps-account/billing/WorkerTimesheetsBillingComponent.js";
import ClientTimesheetsBillingComponent from "./components/fs-account/support-claims/ClientTimesheetsBillingComponent.js";
import config from "./config/config.js";
import ProtectedRoute from "./routes/ProtectedRoute";
import ConditionalProtectedRoute from "./routes/ConditionalProtectedRoute";
import WorkerProfileComponent from "./components/fs-account/searchworkers/WorkerProfileComponent.js";

const App = () => {
  return (
    <UserProvider>
      <AdminUserProvider>
        <ClaimProvider>
          <JobProvider>
            <InvoiceProvider>
              <SearchWorkersProvider>
                <JobCreateProvider>
                  <PSChatProvider>
                    <NotificationProvider>
                      <ApiMetricsProvider>
                        <AgreementProvider>
                          <SessionProvider>
                            <AnalyticsProvider>
                              <TaskSenseProvider>
                              <MainApp />
                              </TaskSenseProvider>
                            </AnalyticsProvider>
                          </SessionProvider>
                        </AgreementProvider>
                      </ApiMetricsProvider>
                    </NotificationProvider>
                  </PSChatProvider>
                </JobCreateProvider>
              </SearchWorkersProvider>
            </InvoiceProvider>
          </JobProvider>
        </ClaimProvider>
      </AdminUserProvider>
    </UserProvider>
  );
};

const MainApp = () => {
  const { clientUser, workerUser, setClientUser, setWorkerUser } = useUser();
  const { adminUser, setAdminUser } = useAdminUser();
  const user = clientUser || workerUser || adminUser;
  const { setClaimsData } = useClaim();

  useEffect(() => {
    connectClientSocket(setClientUser);
    connectWorkerSocket(setWorkerUser);
    connectAdminSocket(setAdminUser);

    return () => {
      disconnectClientSocket();
      disconnectWorkerSocket();
      disconnectAdminSocket();
    };
  }, [setClientUser, setWorkerUser, setAdminUser]);

  useEffect(() => {
    if (user) {
      connectClaimSocket(setClaimsData);

      return () => {
        disconnectClaimSocket();
      };
    }
  }, [user, setClaimsData]);

  return (
    <Router>
      <>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<PasswordResetComponent />} />
          <Route
            path="/secure-password"
            element={<PasswordEnterNewComponent />}
          />
          <Route
            path="/v1/client-users/client-verify-email"
            element={<ClientEmailVerificationPage />}
          />
          <Route
            path="/v1/worker-users/worker-verify-email"
            element={<WorkerEmailVerificationPage />}
          />


          {/* Admin Account Routes */}

          <Route
            path={`${config.ADMIN_ACCOUNT_ROUTE}/super`}
            element={<SuperAdminDashboardComponent />}
          />

          <Route
            path={`${config.ADMIN_ACCOUNT_ROUTE}`}
            element={<AdminAccountDashboard />}
          >
            <Route index element={<MainAdminDashboardComponent />} />
            <Route path=":tab" element={<MainAdminDashboardComponent />} />
            
            <Route
              path="billing-invoicing"
              element={<AdminBillingComponent />}
            />
            <Route
              path="billing-invoicing/:tab"
              element={<AdminBillingComponent />}
            />
            <Route
              path="billing-invoicing/:tab/details"
              element={<AdminBillingComponent />}
            />
            <Route
              path="billing-invoicing/:tab/details/:claimId"
              element={<AdminBillingComponent />}
            />
            <Route
              path="billing-invoicing/:tab/details/:invoiceId"
              element={<AdminBillingComponent />}
            />
            <Route
              path="billing-invoicing/:tab/details/:paymentIntentId"
              element={<AdminBillingComponent />}
            />
            <Route
              path="users"
              element={<UsersWelcomeComponent />}
            />
            <Route
              path="users/worker-users"
              element={<WorkerUsersComponent />}
            >
              <Route
                index
                element={<UsersWelcomeIndexComponent />}
              />

              <Route
                path=":userId"
                element={<WorkerUsersComponent user={user} />}
              />

              <Route path="applied-jobs" element={<JobsAppliedContent />} />
              <Route
                path="applied-jobs/:jobId"
                element={<JobsAppliedContent />}
              />
              <Route path="posted-jobs" element={<JobsPostedContent />} />
              <Route
                path="posted-jobs/:jobId"
                element={<JobsPostedContent />}
              />
            </Route>
            <Route
              path="users/client-users"
              element={<ClientUsersComponent user={user} />}
            >
              <Route
                index
                element={<UsersWelcomeIndexComponent user={user} />}
              />

              <Route
                path=":userId"
                element={<ClientUsersComponent user={user} />}
              />
            </Route>
            <Route
              path="jobs"
              element={<AdminJobListComponent user={user} />}
            />
            <Route
              path="messages"
              element={<AdminMessagesComponent user={user} />}
            />
            <Route
              path="notifications"
              element={<AdminNotificationsComponent user={user} />}
            />
            <Route
              path="jobs/:jobId"
              element={<AdminJobListComponent user={user} />}
            />
            <Route
              path="users/client-users"
              element={<ClientUsersComponent user={user} />}
            />
            
          </Route>
          <Route path="/admin/register" element={<AdminAccountRegister />} />
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Shared Account Route */}
          <Route
            path={`${config.ADMIN_ACCOUNT_ROUTE}/shared`}
            element={
              <ConditionalProtectedRoute
                workerComponent={PSAccountDashboard}
                clientComponent={FSAccountDashboard}
              />
            }
          >

          </Route>
          <Route
            path={`${config.FS_ACCOUNT_ROUTE}`}
            element={<ProtectedRoute><FSAccountDashboard /></ProtectedRoute>}
          >
            <Route index element={<FSDashboardComponent />} />
            <Route path="job-center" element={<JobsComponent user={user} />} />
            <Route path="search-workers" element={<SearchWorkersComponent />} />
            <Route
              path="search-workers/:stepSegment"
              element={<SearchWorkersComponent />}
            />

            <Route
              path="manage-workers/worker-profile/:userId"
              element={<WorkerProfileComponent />}
            />
            <Route
              path="manage-workers"
              element={<ManageWorkersComponent />}
            />
            <Route
              path="manage-workers/:categoryType"
              element={<ManageWorkersComponent />}
            />
            <Route
              path="manage-workers/applied-jobs/:jobId"
              element={<ManageWorkersComponent />} />
            <Route
              path="manage-workers/applied-jobs/:jobId/applicants/:userId"
              element={<WorkerProfileComponent />}
            />
            <Route
              path="manage-workers/agreements/:agreementId"
              element={<ManageWorkersComponent />} />
            <Route
              path="schedules/:userId/:year/:month/:day/:time/:method"
              element={<CalendarComponent user={user} />}
            />
            <Route
              path="my-jobs"
              element={<PostJobsComponent user={user} />}
            />
            <Route
              path="my-jobs/new"
              element={<JobTypeComponent user={user} />}
            />
            <Route
              path="my-jobs/:jobType"
              element={<PostJobsComponent user={user} />}
            />
            <Route
              path="my-jobs/:jobType/:jobId"
              element={<PostJobsComponent user={user} />}
            />
            <Route
              path="my-jobs/new/standard"
              element={<PostJobLocationComponent />}
            />
            <Route
              path="my-jobs/new/urgent"
              element={<PostJobLocationComponent />}
            />
            <Route
              path="my-jobs/new/tasksense/step-ai"
              element={<TaskSenseCraftStep />}
            />
            <Route
              path="my-jobs/new/standard/:stepSegment"
              element={<JobWorkerStepsComponent />}
            />
            <Route
              path="my-jobs/new/urgent/:stepSegment"
              element={<JobWorkerStepsComponent />}
            />
            <Route path="inbox" element={<FSInboxComponent user={user} />} />
            <Route
              path="inbox/:messageId"
              element={<FSInboxComponent user={user} />}
            />

            <Route
              path="agreement-setup"
              element={<AgreementSetupComponent user={user} />}
            />
            <Route
              path="agreement-review"
              element={<AgreementReviewComponent user={user} />}
            />
            <Route
              path="inbox/schedule-confirmation/:messageId"
              element={<FSInboxComponent user={user} />}
            />
            <Route
              path="meeting-options"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting-options/:preferredMethod"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting-options/:preferredMethod/:sessionName/:threadId"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting/:preferredMethod/:sessionName/:threadId"
              element={
                <ProtectedRoute>
                  <MeetingComponent />
                </ProtectedRoute>
              }
            />
            <Route
              path="timesheets-billing"
              element={<ClientTimesheetsBillingComponent user={user} />}
            />
            <Route
              path="timesheets-billing/:tab"
              element={<ClientTimesheetsBillingComponent />}
            />
            <Route
              path="timesheets-billing/details"
              element={<FSSupportClaimsComponent user={user} />}
            />
            <Route
              path="timesheets-billing/details/:claimId"
              element={<FSSupportClaimsComponent user={user} />}
            />
            <Route path="my-account" element={<FSProfileComponent />}>
              <Route index element={<FSPersonalDetailsComponent />} />
              <Route
                path="/account/fs-dashboard/my-account/personal-details"
                element={<FSPersonalDetailsComponent />}
              />
              <Route
                path="/account/fs-dashboard/my-account/emergency-contacts"
                element={<FSMyContactsComponent />}
              />
              <Route
                path="/account/fs-dashboard/my-account/payment-details"
                element={<FSPaymentDetailsComponent />}
              />
              <Route path="account-settings">
                <Route index element={<FSAccountSettingsComponent />} />
                <Route path=":tab" element={<FSAccountSettingsComponent />} />
              </Route>
              <Route
                path="/account/fs-dashboard/my-account/payment-details/payment-setup"
                element={<FSPaymentDetailsComponent />}
              />
            </Route>
          </Route>
          <Route
            path={`${config.PS_ACCOUNT_ROUTE}`}
            element={<ProtectedRoute><PSAccountDashboard /></ProtectedRoute>}
          >
            <Route index element={<PSDashboardComponent />} />
            <Route path="job-listings" element={<JobsComponent />} />
            <Route path="job-listings/:tab" element={<JobsComponent />} />
            <Route path="job-listings/:jobType" element={<JobsComponent />} />
            <Route
              path="job-listings/:jobType/:jobId?"
              element={<JobsComponent />}
            />
            <Route
              path="/account/ps-dashboard/profile"
              element={<PSProfileComponent />}
            />
            <Route path="my-applications"
              element={<MyJobsComponent />} />

            <Route
              path="my-applications/:categoryType"
              element={<MyJobsComponent />}
            />

            <Route
              path="my-applications/applied-jobs/:jobId"
              element={<MyJobsComponent />}
            />

            <Route path="inbox" element={<PSInboxComponent />} />
            <Route path="inbox/:messageId" element={<PSInboxComponent />} />
            <Route
              path="agreement-setup"
              element={<AgreementSetupComponent />}
            />
            <Route
              path="agreement-review"
              element={<AgreementReviewComponent />}
            />
            <Route
              path="meeting-options"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting-options/:preferredMethod"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting-options/:preferredMethod/:sessionName/:threadId"
              element={<MeetingOptionsComponent />}
            />
            <Route
              path="meeting/:preferredMethod/:sessionName/:threadId"
              element={
                <ProtectedRoute>
                  <MeetingComponent />
                </ProtectedRoute>
              }
            />
            <Route path="agreements" element={<MyClientsComponent />} />
            <Route
              path="agreements/:agreementId"
              element={<MyClientsComponent />}
            />
            <Route
              path="timesheets-billing"
              element={<WorkerTimesheetBillingComponent />}
            />
            <Route
              path="timesheets-billing/:tab"
              element={<WorkerTimesheetBillingComponent />}
            />
            <Route
              path="timesheets-billing/details"
              element={<SupportHoursClaimComponent />}
            />
            <Route
              path="timesheets-billing/details/:claimId"
              element={<SupportHoursClaimComponent />}
            />
            <Route
              path="timesheets-billing/new"
              element={<SupportHoursClaimForm />}
            />
            <Route path="news-area" element={<NewsAreaComponent />} />
            <Route
              path="account-settings/:tab"
              element={<PSAccountSettingsComponent />}
            />

            {/* Profile Routes */}

            <Route path="profile" element={<PSProfileComponent user={user} />}>
              <Route index element={<AvailabilityComponent user={user} />} />
              <Route
                path="/account/ps-dashboard/profile/availability"
                element={<AvailabilityComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/service-rates"
                element={<IndicativeRatesComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/locations"
                element={<ProfileLocationsComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/experience"
                element={<ExperienceComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/payment-details"
                element={<BankAccountComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/employment-history"
                element={<WorkHistoryComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/education-and-training"
                element={<EducationAndTrainingComponent user={user} />}
              />
              <Route path="badges" element={<BadgesComponent user={user} />} />
              <Route
                path="/account/ps-dashboard/profile/immunization"
                element={<ImmunizationComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/languages"
                element={<LanguagesComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/cultural-background"
                element={<CulturalBackgroundComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/religion"
                element={<ReligionComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/hobbies"
                element={<InterestsAndHobbiesComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/my-preferences"
                element={<MyPreferencesComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/photo"
                element={<PhotoComponent user={user} />}
              />
              <Route
                path="/account/ps-dashboard/profile/bio"
                element={<BioComponent user={user} />}
              />
            </Route>
          </Route>
          {/* <Route
            path="/register/client"
            element={<FindSupportRegisterSteps />}
          />
          <Route
            path="/register/worker"
            element={<ProvideSupportRegisterSteps />}
          /> */}

          {/* <Route
            path="/register/worker"
            element={<AccountHolderForm />}
          /> */}
          <Route
            path="/register"
            element={<AccountHolderForm />}
          />
          <Route path="/register" element={<WhatBringsYou />} />
          <Route path="/register/client/age" element={<MeRegisterStep />} />
          <Route
            path="/register/client/other"
            element={<FriendFamilyMemberStep />}
          />
          <Route path="/register/mst" element={<MainForm />} />
        </Routes>
      </>
    </Router>
  );
};

export default App;
